import { Link, useLocation } from "@tanstack/react-router";
import { Gauge, Grid3x3, Layers, Plug, Upload, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { DashboardLayout } from "./DashboardLayout";

const settingsTabs = [
  { label: "Users", href: "/dashboard/settings/users", icon: Users },
  { label: "Providers", href: "/dashboard/settings/providers", icon: Plug },
  { label: "Matrix Upload", href: "/dashboard/settings/matrix-upload", icon: Upload },
  { label: "Categories", href: "/dashboard/settings/categories", icon: Grid3x3 },
  { label: "Metrics", href: "/dashboard/settings/metrics", icon: Gauge },
  { label: "Profiles", href: "/dashboard/settings/profiles", icon: Layers },
];

export function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <DashboardLayout>
      {/* Settings header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="mt-1 text-muted-foreground">Manage your workspace configuration.</p>
      </div>

      {/* Tab navigation */}
      <div className="relative mb-8">
        <nav className="flex gap-1 overflow-x-auto">
          {settingsTabs.map((tab) => {
            const isActive = location.pathname === tab.href;

            return (
              <Link
                key={tab.href}
                to={tab.href}
                role="tab"
                aria-selected={isActive}
                className={cn(
                  "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md whitespace-nowrap",
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
                )}
              >
                <tab.icon
                  className={cn(
                    "h-4 w-4 transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground/60 group-hover:text-muted-foreground",
                  )}
                />
                <span>{tab.label}</span>

                {/* Active indicator — accent underline */}
                {isActive && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Baseline border */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </div>

      {/* Tab content */}
      {children}
    </DashboardLayout>
  );
}
